// =====================================================
// 📁 POSCreditNoteProductTable.tsx
// =====================================================

import { useMemo } from 'react';

import { useCreditNoteSaleLookup } from '../../../../../hooks/sale/useCreditNoteSaleLookup';

import POSCreditNoteProductListItem from './POSCreditNoteProductListItem';

interface Props {
  query?: string;
}

const POSCreditNoteProductTable: React.FC<Props> = ({ query = '' }) => {
  // =====================================================
  // 🔹 Hooks
  // =====================================================
  const { selectedSaleDetail } = useCreditNoteSaleLookup();

  // =====================================================
  // 🔹 Products
  // =====================================================
  const products = selectedSaleDetail?.products || [];

  // =====================================================
  // 🔹 Filtered Products
  // =====================================================
  const filteredProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) {
      return products;
    }

    return products.filter((product) =>
      product.productName?.toLowerCase().includes(normalizedQuery)
    );
  }, [products, query]);

  // =====================================================
  // 🎨 Empty
  // =====================================================
  if (!filteredProducts.length) {
    return (
      <div
        className="
          flex
          min-h-[220px]
          items-center
          justify-center

          rounded-2xl
          border
          border-dashed
          border-stroke

          bg-white

          text-center

          dark:border-strokedark
          dark:bg-boxdark
        "
      >
        <div>
          <p
            className="
              text-sm
              font-medium
              text-black

              dark:text-white
            "
          >
            No se encontraron productos
          </p>

          <p
            className="
              mt-1
              text-xs
              text-body
            "
          >
            Intenta cambiar el término de búsqueda.
          </p>
        </div>
      </div>
    );
  }

  // =====================================================
  // 🎨 Render
  // =====================================================
  return (
    <div
      className="
        overflow-hidden

        rounded-2xl

        border
        border-stroke

        bg-white

        shadow-sm

        dark:border-strokedark
        dark:bg-boxdark
      "
    >
      {/* ===================================================== */}
      {/* 🔹 Header */}
      {/* ===================================================== */}
      <div
        className="
          bg-gray-1

          hidden
          items-center

          gap-4
          border-b

          border-stroke

          px-4
          py-3

          text-xs
          font-semibold
          uppercase
          tracking-wide
          text-body

          dark:border-strokedark
          dark:bg-meta-4

          lg:flex
        "
      >
        {/* 🔹 Image */}
        <div className="w-[90px] shrink-0">Imagen</div>

        {/* 🔹 Product */}
        <div className="flex-1">Producto</div>

        {/* 🔹 Sold */}
        <div className="w-[120px] shrink-0 text-center">Vendidos</div>

        {/* 🔹 Credited */}
        <div className="w-[120px] shrink-0 text-center">Acreditados</div>

        {/* 🔹 Available */}
        <div className="w-[120px] shrink-0 text-center">Disponibles</div>

        {/* 🔹 Action */}
        <div className="w-[180px] shrink-0 text-right">Acción</div>
      </div>

      {/* ===================================================== */}
      {/* 🔹 Body */}
      {/* ===================================================== */}
      <div className="flex flex-col">
        {filteredProducts.map((product) => (
          <POSCreditNoteProductListItem
            key={product.productId}
            product={product}
          />
        ))}
      </div>
    </div>
  );
};

export default POSCreditNoteProductTable;
